import { useEffect, useState } from 'react';

const EMPTY = {
    clave: '',
    nombre: '',
    descripcion: '',
    rutaBase: '',
    icono: '',
    activo: true,
    orden: 0,
};

export default function ModulosModal({
    open,
    mode = 'new',
    data = null,
    onClose,
    onSubmit,
    inline = false,
}) {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({
        clave: '',
        nombre: '',
        rutaBase: '',
    });

    const validateField = (field, rawValue) => {
        const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
        let error = '';

        switch (field) {
            case 'clave':
                if (!value) error = 'La clave es obligatoria.';
                break;
            case 'nombre':
                if (!value) error = 'El nombre es obligatorio.';
                break;
            case 'rutaBase':
                if (!value) error = 'La ruta base es obligatoria.';
                break;
            default:
                break;
        }

        setErrors((prev) => ({ ...prev, [field]: error }));
        return !error;
    };

    const validateAll = () => {
        const fields = ['clave', 'nombre', 'rutaBase'];
        let isValid = true;
        fields.forEach(f => {
            if (!validateField(f, form[f])) isValid = false;
        });
        return isValid;
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        validateField(field, value);
    };

    useEffect(() => {
        if (mode === 'edit' && data) {
            const filled = {
                idModulo: data.idModulo ?? 0,
                clave: data.clave ?? '',
                nombre: data.nombre ?? '',
                descripcion: data.descripcion ?? '',
                rutaBase: data.rutaBase ?? '',
                icono: data.icono ?? '',
                activo: data.activo ?? true,
                orden: data.orden ?? 0,
                fechaCreacion: data.fechaCreacion,
            };
            setForm(filled);
            // Validate initial state for required fields
            validateField('clave', filled.clave);
            validateField('nombre', filled.nombre);
            validateField('rutaBase', filled.rutaBase);
        } else {
            setForm(EMPTY);
            setErrors({ clave: '', nombre: '', rutaBase: '' });
        }
    }, [mode, data]);

    if (!open) return null;

    const hasErrors = Object.values(errors).some((e) => !!e);
    const isSaveDisabled =
        !form.clave ||
        !form.nombre ||
        !form.rutaBase ||
        hasErrors;

    const errorStyle = {
        color: '#b91c1c',
        fontSize: '.8rem',
        display: 'block',
        marginTop: '4px',
    };

    const Header = (
        <div
            className="roles-topbar"
            style={{
                marginBottom: '0.6rem',
                paddingRight: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: '0 0 auto',
            }}>
            <h3 className="roles-title" style={{ marginRight: 0 }}>
                {mode === 'new' ? 'Nuevo Módulo' : 'Editar Módulo'}
            </h3>
            <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>
    );

    const Body = (
        <div
            style={{
                paddingLeft: '12%',
                paddingRight: 16,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.8rem',
                overflowY: 'auto',
                flex: '1 1 auto',
                minHeight: 0,
            }}>

            {/* Clave */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Clave:</label>
                <br /><br />
                <input
                    className={`infoField${errors.clave ? ' infoField--error' : ''}`}
                    value={form.clave}
                    onChange={(e) => handleChange('clave', e.target.value)}
                    maxLength={50}
                />
                {errors.clave && <small style={errorStyle}>{errors.clave}</small>}
            </div>

            {/* Nombre */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre:</label>
                <br /><br />
                <input
                    className={`infoField${errors.nombre ? ' infoField--error' : ''}`}
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    maxLength={100}
                />
                {errors.nombre && <small style={errorStyle}>{errors.nombre}</small>}
            </div>

            {/* Ruta Base */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Ruta Base:</label>
                <br /><br />
                <input
                    className={`infoField${errors.rutaBase ? ' infoField--error' : ''}`}
                    value={form.rutaBase}
                    onChange={(e) => handleChange('rutaBase', e.target.value)}
                    maxLength={255}
                    placeholder="/ejemplo"
                />
                {errors.rutaBase && <small style={errorStyle}>{errors.rutaBase}</small>}
            </div>

            {/* Descripción */}
            <div style={{ gridColumn: '1 / 3' }}>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Descripción:</label>
                <br /><br />
                <textarea
                    className="infoField"
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    rows={3}
                    maxLength={500}
                />
            </div>
        </div>
    );

    const Footer = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '.6rem',
                marginTop: '.8rem',
                paddingRight: 16,
                flex: '0 0 auto',
            }}
        >
            <button className="btn-cancel" onClick={onClose}>Cancelar</button>
            <button
                className="btn-action"
                onClick={() => {
                    if (validateAll()) onSubmit(form, mode);
                }}
                disabled={isSaveDisabled}
                style={{
                    opacity: isSaveDisabled ? 0.5 : 1,
                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                }}
            >
                {mode === 'new' ? 'Guardar' : 'Actualizar'}
            </button>
        </div>
    );

    if (inline) {
        return (
            <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {Header}
                {Body}
                {Footer}
            </div>
        );
    }

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(700px, 94vw)',
                    maxHeight: '85vh',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {Header}
                {Body}
                {Footer}
            </div>
        </div>
    );
}
